import type { Beispiel, Ordner, VorlagenStand } from './vorlagenApi';

// Profil-Nachbau F-20 Ergänzung 3 (Bennys Freigabe 14.10.2026, BAU-VORLAGEN.md 1, 2.4, 2.5 und 4): Wörter und reine Regeln der
// Seite „Vorlagen“ und der Auswahl „Vorlage einfügen“. Filter, Suche, Sortierung, Ordner, Platzhalter, der Rückfall der
// Startseite und der Reiter ohne Adresse stehen hier, damit frontend/tests/vorlagen.test.mjs sie ohne Browser prüft. Den
// Produktnamen und den Namen der Firma setzt immer der Aufrufer ein (brand.displayName, MK-1).

// BAU-VORLAGEN.md 2.4: eine Liste für Server (backend/src/administration/vorlagen/texte.ts) und Oberfläche, gleiche Reihenfolge.
export const RECHTSGEBIETE = [
  { schluessel: 'ARBEIT', wort: 'Arbeitsrecht' },
  { schluessel: 'SOZIAL', wort: 'Sozialversicherungsrecht' },
  { schluessel: 'ZIVIL', wort: 'Zivilrecht' },
  { schluessel: 'MIETE', wort: 'Miet- und Wohnrecht' },
  { schluessel: 'FAMILIE_ERBE', wort: 'Familien- und Erbrecht' },
  { schluessel: 'UNTERNEHMEN', wort: 'Unternehmens- und Gesellschaftsrecht' },
  { schluessel: 'VERWALTUNG', wort: 'Verwaltungsrecht' },
  { schluessel: 'STEUER', wort: 'Steuer- und Abgabenrecht' },
  { schluessel: 'STRAF', wort: 'Strafrecht' },
  { schluessel: 'DATENSCHUTZ', wort: 'Datenschutz' },
  { schluessel: 'SONSTIGES', wort: 'Sonstiges' },
] as const;
export type Rechtsgebiet = typeof RECHTSGEBIETE[number]['schluessel'];
export const rechtsgebietWort = (schluessel: string | null | undefined): string | null =>
  RECHTSGEBIETE.find(gebiet => gebiet.schluessel === schluessel)?.wort ?? null;

// Grenzen der Felder wie am Server (BAU-VORLAGEN.md 1.6, 1.7 und 3.2), gezählt nach trim().
export const FELDER = {
  titel: { min: 2, max: 80 },
  kurzbeschreibung: { min: 5, max: 200 },
  text: { min: 10, max: 4000 },
  ordner: { min: 1, max: 60 },
} as const;

export const VORLAGEN_TEXTE = {
  // 1.1 Kopf. F-24 (PF-TX-hilfe-2, SPEC-hilfe-b 3.5 U3): die Unterzeile mit 9 Wörtern wie bei meinGPT („Prompts“).
  titel: 'Vorlagen',
  beschreibung: 'Häufige Fragen einmal formulieren und mit einem Klick übernehmen',
  anlegen: 'Vorlage anlegen',
  // 1.2 Reiter
  meineVorlagen: 'Meine Vorlagen',
  vorlagenDerFirma: 'Vorlagen der Firma',
  beispiele: 'Beispiele',
  reiterMeine: (n: number) => `Meine Vorlagen (${n})`,
  reiterFirma: (n: number) => `Vorlagen der Firma (${n})`,
  reiterBeispiele: (n: number) => `Beispiele (${n})`,
  // 1.3 Werkzeugleiste
  suche: 'Vorlagen durchsuchen',
  suchePlatzhalter: 'Titel, Beschreibung oder Frage',
  alle: 'Alle',
  sortieren: 'Sortieren',
  zuletztVerwendet: 'Zuletzt verwendet',
  zuletztGeaendert: 'Zuletzt geändert',
  titelAZ: 'Titel A–Z',
  reihenfolge: (produkt: string) => `Reihenfolge von ${produkt}`,
  // 1.4 Ordner und Karten
  ohneOrdner: 'Ohne Ordner',
  anzahlVorlagen: (n: number) => (n === 1 ? '1 Vorlage' : `${n} Vorlagen`),
  umbenennen: 'Umbenennen …',
  ordnerLoeschen: 'Ordner löschen …',
  privat: 'Privat',
  ordnerAnlegen: 'Ordner anlegen …',
  zuletztVerwendetAm: (datum: string) => `Zuletzt verwendet am ${datum}`,
  malVerwendet: (n: number) => `${n}-mal verwendet`,
  verwenden: 'Verwenden',
  bearbeiten: 'Bearbeiten …',
  verschieben: 'In Ordner verschieben …',
  kopieren: 'In meine Vorlagen kopieren',
  freigeben: (firma: string) => `Für alle in ${firma} freigeben …`,
  loeschen: 'Löschen …',
  firmaPflegtVerwaltung: (firma: string) => `Die Vorlagen der Firma pflegt die Verwaltung von ${firma}.`,
  // 1.5 Leer, Laden, Fehler
  laden: 'Vorlagen werden geladen …',
  ladeFehler: 'Die Vorlagen konnten nicht geladen werden.',
  erneut: 'Erneut versuchen',
  // F-24 (SPEC-hilfe-b 3.5 V4): ohne den zweiten Satz; die zwei Knöpfe darunter sagen es.
  leerMeine: 'Noch keine eigenen Vorlagen.',
  beispieleAnsehen: 'Beispiele ansehen',
  leerFirma: (firma: string) => `${firma} hat noch keine Vorlagen.`,
  leerFirmaPflege: (firma: string) => `Legen Sie eine an, dann sehen sie alle in ${firma}.`,
  leerBeispiele: 'Noch keine Beispiele veröffentlicht.',
  // F-23 (PF-NB-hilfe-4, Fehlersuche Nr. 11): über den festen Beispielen, solange der Anbieter keines veröffentlicht hat. F-24: ohne
  // den Satz zum Anbieter (SPEC-hilfe-b 3.5 V7).
  festeBeispiele: 'Diese Beispiele zeigt auch die Startseite.',
  keineTreffer: 'Keine Vorlage passt zu Ihrer Suche.',
  sucheZuruecksetzen: 'Suche zurücksetzen',
  // 1.6 Dialog „Vorlage anlegen“ / „Vorlage bearbeiten“ (die Felder bekommen „(Pflicht)“ vom Baustein Field)
  dialogAnlegen: 'Vorlage anlegen',
  dialogBearbeiten: 'Vorlage bearbeiten',
  feldTitel: 'Titel',
  feldKurz: 'Kurzbeschreibung',
  hinweisKurz: 'Steht auf der Karte statt der ganzen Frage.',
  feldFrage: 'Frage',
  zaehler: (n: number) => `${n} von 4000 Zeichen`,
  // hinweisFrage bleibt für die Beispielvorlagen des Betreibers (administration/vorlagen/Beispielvorlagen.tsx).
  hinweisFrage: (produkt: string) => `Angaben in eckigen Klammern, etwa [Branche], markiert ${produkt} beim Übernehmen, damit Sie sie gleich überschreiben. Bitte keine Mandantendaten eintragen.`,
  // F-24 (SPEC-hilfe-b 3.5, T-K): der Satz zu eckigen Klammern als Tooltip am Feld „Frage“; im Hinweis bleibt die Warnung.
  klammernTipp: (produkt: string) => `Angaben in eckigen Klammern, etwa [Branche], markiert ${produkt} beim Übernehmen, damit Sie sie gleich überschreiben.`,
  hinweisMandantendaten: 'Bitte keine Mandantendaten eintragen.',
  schriftstueck: (produkt: string) => `${produkt} recherchiert, verfasst aber keine Schriftstücke. Eine Frage nach einem Mustertext oder einer Vorlage lehnt die Recherche ab.`,
  feldRechtsgebiet: 'Rechtsgebiet',
  ohneRechtsgebiet: 'Ohne Rechtsgebiet',
  feldAblage: 'Ablage',
  nurFuerMich: 'Nur für mich',
  fuerAlleIn: (firma: string) => `Für alle in ${firma}`,
  feldOrdner: 'Ordner',
  abbrechen: 'Abbrechen',
  speichern: 'Speichern',
  gespeichert: (titel: string) => `Die Vorlage „${titel}“ ist gespeichert.`,
  veraltet: 'Die Vorlage wurde inzwischen geändert.',
  mindestens: (n: number) => `Bitte mindestens ${n} Zeichen eintragen.`,
  // 1.7 Weitere Dialoge
  verschiebenTitel: 'In Ordner verschieben',
  verschiebenKnopf: 'Verschieben',
  verschobenIn: (ordner: string) => `Die Vorlage liegt jetzt in „${ordner}“.`,
  verschobenOhne: 'Die Vorlage liegt jetzt ohne Ordner.',
  freigebenTitel: (firma: string) => `Für alle in ${firma} freigeben`,
  freigebenSatz: 'Eine Kopie erscheint unter „Vorlagen der Firma“. Ihre eigene Vorlage bleibt unverändert.',
  feldOrdnerFirma: 'Ordner der Firma',
  freigebenKnopf: 'Freigeben',
  freigegeben: (firma: string) => `Für alle in ${firma} freigegeben.`,
  kopiert: 'In „Meine Vorlagen“ kopiert.',
  loeschenFrage: (titel: string) => `Vorlage „${titel}“ löschen?`,
  loeschenPerson: 'Sie lässt sich nicht wiederherstellen.',
  loeschenFirma: (firma: string) => `Sie verschwindet für alle in ${firma} und lässt sich nicht wiederherstellen.`,
  loeschenKnopf: 'Löschen',
  ordnerAnlegenTitel: 'Ordner anlegen',
  ordnerUmbenennenTitel: 'Ordner umbenennen',
  feldName: 'Name',
  ordnerAngelegt: (name: string) => `Der Ordner „${name}“ ist angelegt.`,
  ordnerUmbenannt: (name: string) => `Der Ordner „${name}“ ist umbenannt.`,
  ordnerLoeschenFrage: (name: string) => `Ordner „${name}“ löschen?`,
  ordnerLoeschenSatz: (n: number) => (n === 1 ? 'Die 1 Vorlage darin bleibt erhalten und steht danach unter „Ohne Ordner“.'
    : `Die ${n} Vorlagen darin bleiben erhalten und stehen danach unter „Ohne Ordner“.`),
  ordnerLoeschenKnopf: 'Ordner löschen',
  grenzeVorlagen: (n: number) => `Sie haben die Höchstzahl von ${n} Vorlagen erreicht.`,
  grenzeOrdner: (n: number) => `Sie haben die Höchstzahl von ${n} Ordnern erreicht.`,
  // 4 Auswahl „Vorlage einfügen“
  einfuegen: 'Vorlage einfügen',
  ersetzt: 'Ihr Text im Eingabefeld wird dabei ersetzt.',
  verwalten: 'Vorlagen verwalten',
  auswahlLeer: 'Noch keine Vorlagen. Unter „Vorlagen“ legen Sie eine an.',
  schliessen: 'Dialog schließen',
} as const;

const T = VORLAGEN_TEXTE;

/** Fehler am Feld: zu kurz nach trim(). Ein Feld mit Mindestlänge 1 nennt nur „Es fehlt noch: …“ unter den Knöpfen. */
export const zuKurz = (wert: string, min: number): string | undefined => (min > 1 && wert.trim().length < min ? T.mindestens(min) : undefined);

// TT.MM.JJJJ in österreichischer Zeit (de-AT).
const DATUM = new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Vienna' });
export const datumText = (iso: string) => DATUM.format(new Date(iso));

type Durchsuchbar = { titel: string; kurzbeschreibung: string; text: string; rechtsgebiet: string | null };

/** 1.3: Suche in Titel, Kurzbeschreibung und Text ohne Groß- und Kleinschreibung, Umlaute wie geschrieben; dazu das Rechtsgebiet. */
export function filtereVorlagen<V extends Durchsuchbar>(liste: readonly V[], filter: { suche?: string; rechtsgebiet?: string | null }): V[] {
  const suche = (filter.suche ?? '').trim().toLocaleLowerCase('de');
  return liste.filter(vorlage => (!filter.rechtsgebiet || vorlage.rechtsgebiet === filter.rechtsgebiet)
    && (!suche || [vorlage.titel, vorlage.kurzbeschreibung, vorlage.text].some(feld => feld.toLocaleLowerCase('de').includes(suche))));
}

/** 1.3: „Alle“ und nur die Rechtsgebiete, die in der Liste vorkommen, in der Reihenfolge von 2.4. */
export const rechtsgebieteIn = (liste: readonly { rechtsgebiet: string | null }[]) =>
  RECHTSGEBIETE.filter(gebiet => liste.some(vorlage => vorlage.rechtsgebiet === gebiet.schluessel));

export type Sortierung = 'verwendet' | 'geaendert' | 'titel' | 'reihenfolge';
const nachTitel = (a: { titel: string }, b: { titel: string }) => a.titel.localeCompare(b.titel, 'de');
const zeitpunkt = (wert: string | null | undefined) => (wert ? Date.parse(wert) : Number.NaN);

/** 1.3: „Zuletzt verwendet“ (ohne Datum ans Ende, dann Titel), „Zuletzt geändert“, „Titel A–Z“; „Reihenfolge“ lässt die Liste,
 *  wie der Server sie schickt. */
export function sortiereVorlagen<V extends { titel: string; zuletztVerwendetAm?: string | null; updatedAt?: string }>(liste: readonly V[], art: Sortierung): V[] {
  const kopie = [...liste];
  if (art === 'reihenfolge') return kopie;
  if (art === 'titel') return kopie.sort(nachTitel);
  const wert = (vorlage: V) => zeitpunkt(art === 'verwendet' ? vorlage.zuletztVerwendetAm : vorlage.updatedAt);
  return kopie.sort((a, b) => {
    const x = wert(a), y = wert(b);
    if (Number.isNaN(x) || Number.isNaN(y)) return Number.isNaN(x) && Number.isNaN(y) ? nachTitel(a, b) : Number.isNaN(x) ? 1 : -1;
    return y - x || nachTitel(a, b);
  });
}

/** 1.4: erst die Vorlagen ohne Ordner (auch die mit einem Ordner, den es nicht mehr gibt), dann je Ordner nach Titel A–Z. */
export function ordnerGruppen<V extends { ordnerId: string | null }>(vorlagen: readonly V[], ordner: readonly Ordner[]) {
  const bekannt = new Set(ordner.map(eintrag => eintrag.id));
  return {
    ohne: vorlagen.filter(vorlage => !vorlage.ordnerId || !bekannt.has(vorlage.ordnerId)),
    ordner: [...ordner].sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .map(eintrag => ({ ordner: eintrag, vorlagen: vorlagen.filter(vorlage => vorlage.ordnerId === eintrag.id) })),
  };
}

// 4 (V-6): der erste Platzhalter in eckigen Klammern; der Aufrufer markiert ihn nach dem Einfügen mit setSelectionRange.
const PLATZHALTER = /\[[^\]\n]{1,60}\]/;
export function ersterPlatzhalter(text: string): { start: number; ende: number } | null {
  const treffer = PLATZHALTER.exec(text);
  return treffer ? { start: treffer.index, ende: treffer.index + treffer[0].length } : null;
}

// 2.5 (V-9): bittet der Text um ein Schriftstück? Derselbe Ausdruck gilt am Server (backend/src/administration/vorlagen/
// pruefung.ts, PF-903). Erst fallen die Ausnahmen des Recherche-Wächters weg (QueryGuard.ts: „Vorlage an den EuGH/VfGH“,
// Vorabentscheidung, Musterverfahren), dann zählt ein Wortanfang ohne Groß- und Kleinschreibung.
export const SCHRIFTSTUECK_AUSNAHMEN = /vorlage an den (?:eugh|vfgh)|vorabentscheidung|muster-?verfahren/giu;
export const SCHRIFTSTUECK_WOERTER = /(?<![\p{L}\p{N}])(?:muster|vorlage|textbaustein|verfasse|formuliere|entwirf|entwerfe|schreibe?\s+mir)/iu;
export const bittetUmSchriftstueck = (text: string) => SCHRIFTSTUECK_WOERTER.test(text.replace(SCHRIFTSTUECK_AUSNAHMEN, ' '));

// 5.1 (V-7): die Karten der Startseite. Ohne veröffentlichtes Beispiel mit „Auf der Startseite“ bleiben die drei festen
// Beispiele, wörtlich aus pages/Dashboard.tsx (researchExamples). symbol nennt das Heroicon, das die Startseite zeigt.
export type Beispielsymbol = 'BookOpenIcon' | 'ScaleIcon' | 'BriefcaseIcon';
export type Beispielkarte = { id: string | null; title: string; scope: string; question: string; symbol: Beispielsymbol };
export const FESTE_BEISPIELE: readonly Beispielkarte[] = [
  { id: null, title: 'Gesetze', scope: 'Bundes- und Landesrecht', question: 'Welche Gewährleistungsfristen gelten beim Kauf beweglicher Sachen?', symbol: 'BookOpenIcon' },
  { id: null, title: 'Judikatur', scope: 'Entscheidungen & Rechtssätze', question: 'Wie beurteilt der OGH Kreditbearbeitungsgebühren?', symbol: 'ScaleIcon' },
  { id: null, title: 'Kollektivverträge', scope: 'Regelungen Ihrer Branche', question: 'Was regelt der Handels-KV für Angestellte zur Mehrarbeit?', symbol: 'BriefcaseIcon' },
];
// F-23 (PF-NB-hilfe-4; Fehlersuche Nr. 11, Nutzerblick Mitglied H-07: Startseite mit drei Beispielen, „Vorlagen“ mit „Beispiele (0)“):
// dieselben drei Fragen als Beispiele für den Reiter „Beispiele“ und „Vorlage einfügen“, solange der Server keines liefert. Titel,
// Rechtsgebiet und Kurzbeschreibung wie die vorbereiteten Beispiele 1 bis 3 (Migration 20261014165000_vorlagen); die Kennungen
// fest-… gehen nie an den Server (istFestesBeispiel).
export const FESTE_BEISPIEL_VORLAGEN: readonly Beispiel[] = [
  { id: 'fest-1', rechtsgebiet: 'ZIVIL', titel: 'Gewährleistung beim Kauf', kurzbeschreibung: 'Fristen und Beginn der Gewährleistung bei beweglichen Sachen.', text: FESTE_BEISPIELE[0].question },
  { id: 'fest-2', rechtsgebiet: 'ZIVIL', titel: 'Kreditbearbeitungsgebühren', kurzbeschreibung: 'Wie der OGH Bearbeitungsentgelte in Kreditverträgen beurteilt.', text: FESTE_BEISPIELE[1].question },
  { id: 'fest-3', rechtsgebiet: 'ARBEIT', titel: 'Mehrarbeit im Handels-KV', kurzbeschreibung: 'Was der Kollektivvertrag für Handelsangestellte zur Mehrarbeit regelt.', text: FESTE_BEISPIELE[2].question },
];
export const istFestesBeispiel = (id: string) => id.startsWith('fest-');
/** Die Beispiele des Servers, ohne eines die drei festen (wie startBeispiele für die Startseite). */
export const beispieleMitRueckfall = (beispiele: readonly Beispiel[]): readonly Beispiel[] => (beispiele.length ? beispiele : FESTE_BEISPIEL_VORLAGEN);
const SYMBOL_JE_GEBIET: Partial<Record<string, Beispielsymbol>> = {
  ARBEIT: 'BriefcaseIcon', ZIVIL: 'ScaleIcon', MIETE: 'ScaleIcon', FAMILIE_ERBE: 'ScaleIcon', UNTERNEHMEN: 'ScaleIcon',
};
/** Antwort von GET /vorlagen/startseite → höchstens drei Karten; ohne Antwort, ohne Beispiel oder bei Fehler die festen drei. */
export function startBeispiele(antwort?: { beispiele?: readonly Beispiel[] | null } | null): Beispielkarte[] {
  const liste = antwort?.beispiele ?? [];
  if (!liste.length) return [...FESTE_BEISPIELE];
  return liste.slice(0, 3).map(beispiel => ({ id: beispiel.id, title: beispiel.titel, scope: rechtsgebietWort(beispiel.rechtsgebiet) ?? '',
    question: beispiel.text, symbol: SYMBOL_JE_GEBIET[beispiel.rechtsgebiet] ?? 'BookOpenIcon' }));
}

// 1.2: Reiter in der Adresse /vorlagen?reiter=meine|firma|beispiele. Ohne gültige Adresse „Meine Vorlagen“, wenn es eigene
// gibt, sonst „Beispiele“; „firma“ nur mit Firma (V-3).
export type Reiter = 'meine' | 'firma' | 'beispiele';
export const standardReiter = (daten: Pick<VorlagenStand, 'meine'>): Reiter => (daten.meine.vorlagen.length > 0 ? 'meine' : 'beispiele');
export function reiterAusAdresse(wert: string | null, daten: Pick<VorlagenStand, 'meine' | 'firma'>): Reiter {
  if (wert === 'meine' || wert === 'beispiele') return wert;
  if (wert === 'firma' && daten.firma) return 'firma';
  return standardReiter(daten);
}

// 4: Gruppen der Auswahl in fester Reihenfolge, je nur mit Einträgen: bis 5 persönliche mit Datum, dann alle eigenen und die
// der Firma (Titel A–Z), dann die Beispiele in der Reihenfolge des Betreibers. F-23: ohne Beispiel des Servers die drei festen.
export type AuswahlEintrag = { art: 'vorlage' | 'beispiel'; id: string; titel: string; kurzbeschreibung: string; text: string };
export type AuswahlGruppe = { schluessel: 'zuletzt' | 'meine' | 'firma' | 'beispiele'; titel: string; eintraege: AuswahlEintrag[] };
export function auswahlGruppen(daten: VorlagenStand, suche: string): AuswahlGruppe[] {
  const eintrag = (art: AuswahlEintrag['art']) => (v: { id: string; titel: string; kurzbeschreibung: string; text: string }): AuswahlEintrag =>
    ({ art, id: v.id, titel: v.titel, kurzbeschreibung: v.kurzbeschreibung, text: v.text });
  const meine = filtereVorlagen(daten.meine.vorlagen, { suche });
  const gruppen: AuswahlGruppe[] = [
    { schluessel: 'zuletzt', titel: T.zuletztVerwendet,
      eintraege: sortiereVorlagen(meine.filter(v => v.zuletztVerwendetAm), 'verwendet').slice(0, 5).map(eintrag('vorlage')) },
    { schluessel: 'meine', titel: T.meineVorlagen, eintraege: sortiereVorlagen(meine, 'titel').map(eintrag('vorlage')) },
    { schluessel: 'firma', titel: T.vorlagenDerFirma, eintraege: sortiereVorlagen(filtereVorlagen(daten.firma?.vorlagen ?? [], { suche }), 'titel').map(eintrag('vorlage')) },
    { schluessel: 'beispiele', titel: T.beispiele, eintraege: filtereVorlagen(beispieleMitRueckfall(daten.beispiele), { suche }).map(eintrag('beispiel')) },
  ];
  return gruppen.filter(gruppe => gruppe.eintraege.length > 0);
}
